import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Combobox } from '../ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Search, Edit, ArrowUp, ArrowDown } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import { cascadeMasterUpdate, confirmAndDeleteMaster } from '../../utils/masterCascade';

// Map API endpoint paths → master_type keys understood by the backend cascade
// endpoint. Anything not in this map falls back to a plural-stripping heuristic.
const ENDPOINT_TO_MASTER_TYPE = {
  '/rate-lists': 'rate-list',
  '/transports': 'transport',
  '/priorities': 'priority',
  '/colours': 'colour',
  '/printing-substrates': 'printing-substrate',
  '/lamination-substrates': 'lamination-substrate',
  '/credibility-ratings': 'credibility-rating',
  '/bs-suppliers': 'supplier',
  '/process-status': 'process-status',
  '/design-status': 'design-status',
  '/defect-list': 'defect-list',
  '/designations': 'designation',
};
const deriveMasterType = (endpoint) => {
  if (ENDPOINT_TO_MASTER_TYPE[endpoint]) return ENDPOINT_TO_MASTER_TYPE[endpoint];
  const last = (endpoint || '').split('/').filter(Boolean).pop() || '';
  return last.endsWith('s') ? last.slice(0, -1) : last;
};

// Generic Master Table Management Component with Edit support
const MasterTableManagement = ({ 
  title, 
  apiEndpoint, 
  fields,
  displayField,
  secondaryField,
  onBeforeSubmit,
  reorderable = false,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    setLoading(true);
    setItems([]);
    fetchItems();
    resetForm();
  }, [apiEndpoint]);

  const resetForm = () => {
    const initialData = {};
    fields.forEach(f => {
      initialData[f.name] = f.type === 'multiselect' ? [] : '';
    });
    setFormData(initialData);
  };

  const fetchItems = async () => {
    try {
      const response = await apiClient.get(apiEndpoint);
      setItems(response.data);
    } catch (error) {
      toast.error(`Failed to fetch ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = fields.filter(f => f.required && f.type !== 'hidden');
    for (const field of requiredFields) {
      if (!formData[field.name] || (Array.isArray(formData[field.name]) && formData[field.name].length === 0)) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    try {
      const submitData = onBeforeSubmit ? onBeforeSubmit(formData) : formData;
      await apiClient.post(apiEndpoint, submitData);
      toast.success(`${title} added successfully`);
      fetchItems();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to add ${title.toLowerCase()}`);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const data = {};
    fields.forEach(f => {
      data[f.name] = item[f.name] || (f.type === 'multiselect' ? [] : '');
    });
    setEditFormData(data);
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    // Validate required fields
    const requiredFields = fields.filter(f => f.required && f.type !== 'hidden');
    for (const field of requiredFields) {
      if (!editFormData[field.name] || (Array.isArray(editFormData[field.name]) && editFormData[field.name].length === 0)) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    try {
      const submitData = onBeforeSubmit ? onBeforeSubmit(editFormData) : editFormData;
      const oldValue = editingItem?.[displayField];
      await apiClient.put(`${apiEndpoint}/${editingItem.id}`, submitData);
      toast.success(`${title} updated successfully`);
      // Propagate rename to downstream records
      const newValue = submitData?.[displayField];
      if (oldValue && newValue && oldValue !== newValue) {
        await cascadeMasterUpdate(deriveMasterType(apiEndpoint), oldValue, newValue);
      }
      fetchItems();
      setShowEditModal(false);
      setEditingItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update ${title.toLowerCase()}`);
    }
  };

  const handleDelete = async (id) => {
    const item = items.find(i => i.id === id);
    const label = item ? (item[displayField] || String(item.id)) : String(id);
    const ok = await confirmAndDeleteMaster({
      masterType: deriveMasterType(apiEndpoint),
      valueLabel: label,
      deleteMasterFn: async () => { await apiClient.delete(`${apiEndpoint}/${id}`); },
    });
    if (ok) fetchItems();
  };

  const handleMove = async (index, direction) => {
    // direction: -1 = up, +1 = down. Operates on the current (unfiltered) items array.
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);  // optimistic
    try {
      await apiClient.post(`${apiEndpoint}/reorder`, {
        ordered_ids: next.map(i => i.id),
      });
    } catch (error) {
      toast.error('Failed to reorder');
      fetchItems();  // revert
    }
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (String(item[displayField] || '').toLowerCase().includes(searchLower)) ||
      (secondaryField && String(item[secondaryField] || '').toLowerCase().includes(searchLower))
    );
  });

  const renderField = (field, data, setData) => {
    if (field.type === 'hidden') return null;
    if (field.type === 'select') {
      return (
        <Combobox
          options={field.options.map(o => ({ value: o.value || o, label: o.label || o }))}
          value={data[field.name] || ''}
          onValueChange={(v) => setData({ ...data, [field.name]: v })}
          placeholder={`Select ${field.label.toLowerCase()}...`}
        />
      );
    }
    
    if (field.type === 'multiselect') {
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {(data[field.name] || []).map((val, idx) => (
              <span key={idx} className="px-2 py-1 bg-slate-100 rounded text-sm flex items-center gap-1">
                {val}
                <button
                  type="button"
                  onClick={() => {
                    const newVals = data[field.name].filter((_, i) => i !== idx);
                    setData({ ...data, [field.name]: newVals });
                  }}
                  className="text-slate-500 hover:text-red-500"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <Combobox
            options={field.options.filter(o => !(data[field.name] || []).includes(o.value || o)).map(o => ({ value: o.value || o, label: o.label || o }))}
            value=""
            onValueChange={(v) => {
              if (v && !(data[field.name] || []).includes(v)) {
                setData({ ...data, [field.name]: [...(data[field.name] || []), v] });
              }
            }}
            placeholder={`Add ${field.label.toLowerCase()}...`}
          />
        </div>
      );
    }
    
    if (field.type === 'number') {
      return (
        <Input
          type="number"
          value={data[field.name] || ''}
          onChange={(e) => setData({ ...data, [field.name]: e.target.value })}
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      );
    }
    
    // Default: text input
    return (
      <Input
        value={data[field.name] || ''}
        onChange={(e) => setData({ ...data, [field.name]: e.target.value })}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
    );
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Add Form */}
      <Card className="p-6 bg-white rounded-lg border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New {title}</h3>
        <form onSubmit={handleSubmit}>
          <div className={`grid gap-4 ${fields.filter(f=>f.type!=='hidden').length > 2 ? 'grid-cols-3' : fields.filter(f=>f.type!=='hidden').length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {fields.map((field) => field.type === 'hidden' ? null : (
              <div key={field.name}>
                <Label className="mb-2 block">{field.label} {field.required && '*'}</Label>
                {renderField(field, formData, setFormData)}
              </div>
            ))}
          </div>
          <Button type="submit" className="mt-4 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add {title}
          </Button>
        </form>
      </Card>

      {/* List */}
      <Card className="p-6 bg-white rounded-lg border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">{title} List ({filteredItems.length})</h3>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No {title.toLowerCase()} found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                  {fields.map(f => (
                    <th key={f.name} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      {f.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item, index) => {
                  // Real index in the unfiltered items[] (needed for reorder)
                  const realIndex = items.findIndex(i => i.id === item.id);
                  const reorderDisabled = reorderable && Boolean(searchQuery);
                  return (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">{index + 1}</td>
                    {fields.map(f => (
                      <td key={f.name} className="px-4 py-3 text-sm text-slate-900">
                        {Array.isArray(item[f.name]) 
                          ? item[f.name].join(', ') || '-'
                          : item[f.name] || '-'}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {reorderable && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={reorderDisabled || realIndex <= 0}
                              onClick={() => handleMove(realIndex, -1)}
                              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2"
                              title={reorderDisabled ? 'Clear search to reorder' : 'Move up'}
                              data-testid={`master-move-up-${item.id}`}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={reorderDisabled || realIndex >= items.length - 1}
                              onClick={() => handleMove(realIndex, 1)}
                              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2"
                              title={reorderDisabled ? 'Clear search to reorder' : 'Move down'}
                              data-testid={`master-move-down-${item.id}`}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {fields.map((field) => field.type === 'hidden' ? null : (
              <div key={field.name}>
                <Label className="mb-2 block">{field.label} {field.required && '*'}</Label>
                {renderField(field, editFormData, setEditFormData)}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditSubmit}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterTableManagement;
